import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AuthenticationService } from '../authentication/authentication.service';
import CronjobInfo from '../models/CronjobInfo';
import Project from '../models/Project';
import { TIMESHEET_STATUS, Timesheet } from '../models/Timesheet';
import { Timeslot } from '../models/Timeslot';

@Injectable({
    providedIn: 'root',
})
export class TimesheetsService {
    httpOptions = {};

    constructor(private httpClient: HttpClient, private authenticationService: AuthenticationService) {
        this.httpOptions = authenticationService.getHeaders();
    }

    syncJira(timesheetId: string){
        return this.httpClient.get(`${environment.baseUrl}/jira/syncJira/${timesheetId}`, this.httpOptions); 
    }

    getTimesheets(date: string) {
        return this.httpClient
            .get<Timesheet[]>(`${environment.baseUrl}/timesheets${this.getCorrectRoute()}/date/${date}${this.getTokenIfExists()}`, this.httpOptions)
            .pipe(map((timesheets) => this.parseTimesheetsStatus(timesheets)));
    }

    getTimesheetsByProjectUuid(projectUuid: string) {
        return this.httpClient.get<Timesheet[]>(`${environment.baseUrl}/timesheets/project/${projectUuid}`, this.httpOptions);
    }

    addTimesheet(employeeUuid: string, date: string, hoursRequired?: number) {
        const parsedDate = new Date(date);
        return this.httpClient.post<Timesheet>(
            `${environment.baseUrl}/timesheets`,
            {
                employeeUuid: employeeUuid,
                date: parsedDate,
                hoursRequired: hoursRequired ? hoursRequired : 0,
            },
            this.httpOptions,
        );
    }

    updateTimesheet(timesheet: Timesheet) {
        const body = {
            timesheetId: timesheet.uuid,
            extraInfo: timesheet.extraInfo,
            happyScore: timesheet.happyScore,
            status: timesheet.status.key,
        };

        return this.httpClient
            .put<Timesheet>(`${environment.baseUrl}/timesheets${this.getCorrectRoute()}${this.getTokenIfExists()}`, body, this.httpOptions)
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    removeTimesheet(timesheetUuid: string) {
        return this.httpClient.delete<boolean>(`${environment.baseUrl}/timesheets/${timesheetUuid}`, this.httpOptions);
    }

    addTimeslot(timesheetUuid: string, projectUuid: string, minutes: number, pdfUrl: string, description: string) {
        return this.httpClient
            .post<Timesheet>(
                `${
                    environment.baseUrl
                }/timesheets${this.getCorrectRoute()}/timeslot/${timesheetUuid}${this.getTokenIfExists()}?projectUuid=${projectUuid}&minutes=${minutes}&pdf=${pdfUrl}&description=${description}`,
                {},
                this.httpOptions,
            )
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    updateTimeslot(timeslot: Timeslot) {
        let projectUuid = '';
        if (timeslot.project != null) {
            projectUuid = '?projectUuid=';
            projectUuid += timeslot.project.uuid;
            timeslot.project = null;
        }
        return this.httpClient
            .put<Timesheet>(
                `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/timeslot${this.getTokenIfExists()}${projectUuid}`,
                timeslot,
                this.httpOptions,
            )
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    public setTimeslotToIsBilled(
        timeslotUuid: string,
        invoiceNumber?: string,
        invoiceId?: string,
        invoiceStatus?: string,
        invoiceDate?: string,
        invoicePdfUrl?: string,
        invoiceLink?: string,
    ) {
        let params = '';
        const parts: string[] = [];
        if (invoiceNumber) parts.push(`invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
        if (invoiceId) parts.push(`invoiceId=${encodeURIComponent(invoiceId)}`);
        if (invoiceStatus) parts.push(`invoiceStatus=${encodeURIComponent(invoiceStatus)}`);
        if (invoiceDate) parts.push(`invoiceDate=${encodeURIComponent(invoiceDate)}`);
        if (invoicePdfUrl) parts.push(`invoicePdfUrl=${encodeURIComponent(invoicePdfUrl)}`);
        if (invoiceLink) parts.push(`invoiceLink=${encodeURIComponent(invoiceLink)}`);
        if (parts.length) params = '?' + parts.join('&');
        return this.httpClient.put<boolean>(`${environment.baseUrl}/timesheets/timeslot/bill/${timeslotUuid}${params}`, null, this.httpOptions);
    }

    public unbillTimeslot(timeslotUuid: string) {
        return this.httpClient.put<boolean>(`${environment.baseUrl}/timesheets/timeslot/unbill/${timeslotUuid}`, null, this.httpOptions);
    }

    public setNoInvoice(timeslotUuid: string, reason: string, attachment?: File) {
        const formData = new FormData();
        formData.append('reason', reason);
        if (attachment) {
            formData.append('attachment', attachment);
        }
        // Do NOT include Content-Type header — browser must set it automatically for multipart/form-data (with boundary)
        const authToken = (this.httpOptions as any)?.headers?.get?.('Authorization') ?? '';
        const options = authToken ? { headers: new HttpHeaders({ Authorization: authToken }) } : {};
        return this.httpClient.put<boolean>(`${environment.baseUrl}/timesheets/timeslot/${timeslotUuid}/no-invoice`, formData, options);
    }

    public clearNoInvoice(timeslotUuid: string) {
        return this.httpClient.delete<boolean>(`${environment.baseUrl}/timesheets/timeslot/${timeslotUuid}/no-invoice`, this.httpOptions);
    }

    public addTimeslotInvoiceAllocation(
        timeslotUuid: string,
        allocatedMinutes?: number,
        amountExcludingVat?: number,
        rateType?: string,
        sesDocumentId?: string,
        sesNumber?: string,
        poDocumentId?: string,
        poNumber?: string,
        invoiceNumber?: string,
        invoiceId?: string,
        invoiceStatus?: string,
        invoiceDate?: string,
        invoicePdfUrl?: string,
        invoiceLink?: string,
    ) {
        const parts: string[] = [];
        if (allocatedMinutes !== undefined && allocatedMinutes !== null) parts.push(`allocatedMinutes=${encodeURIComponent(String(allocatedMinutes))}`);
        if (amountExcludingVat !== undefined && amountExcludingVat !== null) parts.push(`amountExcludingVat=${encodeURIComponent(String(amountExcludingVat))}`);
        if (rateType) parts.push(`rateType=${encodeURIComponent(rateType)}`);
        if (sesDocumentId) parts.push(`sesDocumentId=${encodeURIComponent(sesDocumentId)}`);
        if (sesNumber) parts.push(`sesNumber=${encodeURIComponent(sesNumber)}`);
        if (poDocumentId) parts.push(`poDocumentId=${encodeURIComponent(poDocumentId)}`);
        if (poNumber) parts.push(`poNumber=${encodeURIComponent(poNumber)}`);
        if (invoiceNumber) parts.push(`invoiceNumber=${encodeURIComponent(invoiceNumber)}`);
        if (invoiceId) parts.push(`invoiceId=${encodeURIComponent(invoiceId)}`);
        if (invoiceStatus) parts.push(`invoiceStatus=${encodeURIComponent(invoiceStatus)}`);
        if (invoiceDate) parts.push(`invoiceDate=${encodeURIComponent(invoiceDate)}`);
        if (invoicePdfUrl) parts.push(`invoicePdfUrl=${encodeURIComponent(invoicePdfUrl)}`);
        if (invoiceLink) parts.push(`invoiceLink=${encodeURIComponent(invoiceLink)}`);
        const params = parts.length ? '?' + parts.join('&') : '';
        return this.httpClient.post<boolean>(`${environment.baseUrl}/timesheets/timeslot/${timeslotUuid}/invoice-allocation${params}`, null, this.httpOptions);
    }

    removeTimeslot(timeslotUuid: string) {
        return this.httpClient.delete(
            `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/timeslot/${timeslotUuid}${this.getTokenIfExists()}`,
            this.httpOptions,
        );
    }

    getPdf(timeslotUuid: string) {
        return this.httpClient.get<any>(
            `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/retrieveFile/${timeslotUuid}${this.getTokenIfExists()}`,
            this.httpOptions,
        );
    }

    // ===== Validation metadata CRUD =====
    saveTimeslotValidation(timeslotUuid: string, payload: { consultantName?: string; timesheetType?: string; periodISO?: string; signed?: boolean; hours?: number; }) {
        return this.httpClient
            .put<Timesheet>(
                `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/timeslot/${timeslotUuid}/validation${this.getTokenIfExists()}`,
                payload,
                this.httpOptions,
            )
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    fetchTimeslotValidation(timeslotUuid: string) {
        return this.httpClient.get<any>(
            `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/timeslot/${timeslotUuid}/validation${this.getTokenIfExists()}`,
            this.httpOptions,
        );
    }

    clearTimeslotValidation(timeslotUuid: string) {
        return this.httpClient
            .delete<Timesheet>(
                `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/timeslot/${timeslotUuid}/validation${this.getTokenIfExists()}`,
                this.httpOptions,
            )
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    getTimesheetByUserAndDate(userId: string, date: string): Observable<Timesheet> {
        return this.httpClient
            .get<Timesheet>(`${environment.baseUrl}/timesheets${this.getCorrectRoute()}/user/${userId}/${date}${this.getTokenIfExists()}`, this.httpOptions)
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    sendReminders(selectedDate: string) {
        return this.httpClient.get<boolean>(`${environment.baseUrl}/timesheets/reminders/${selectedDate}`, this.httpOptions);
    }

    sendReminder(timesheetId: string) {
        return this.httpClient.get<boolean>(`${environment.baseUrl}/timesheets/reminder/${timesheetId}`, this.httpOptions);
    }

    addReminderRule(daysBeforeEnd: number) {
        return this.httpClient.post<CronjobInfo>(`${environment.baseUrl}/timesheets/reminders/rules/${daysBeforeEnd}`, {}, this.httpOptions);
    }

    getReminderRule() {
        return this.httpClient.get<CronjobInfo[]>(`${environment.baseUrl}/timesheets/reminders/rules`, this.httpOptions);
    }

    deleteReminderRule(cron: CronjobInfo) {
        const triggerName: string = cron.name + cron.cron;
        const group: string = cron.group;
        const params: string = '?triggerName=' + triggerName + '&groupName=' + group;
        return this.httpClient.delete<boolean>(`${environment.baseUrl}/timesheets/reminders/rules${params}`, this.httpOptions);
    }

    updateExtraInfo(extraInfo: string, timesheetUuid?: string) {
        return this.httpClient
            .put<Timesheet>(
                `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/extra_info${
                    this.getTokenIfExists() != '' ? this.getTokenIfExists() : '/' + timesheetUuid
                }`,
                extraInfo,
                this.httpOptions,
            )
            .pipe(map((timesheet) => this.parseTimesheetStatus(timesheet)));
    }

    getTokenIfExists() {
        // Preserve the token regardless of login status to allow public routes
        const token = sessionStorage.getItem('timesheetToken');
        return token ? `/${token}` : ``;
    }

    getCorrectRoute() {
        // If a public timesheet token exists, route to the management controller
        return sessionStorage.getItem('timesheetToken') ? 'management' : '';
    }

    isLoggedIn(): boolean {
        return localStorage.getItem('token') != undefined && localStorage.getItem('token') != null;
    }

    parseTimesheetsStatus(timesheets: Timesheet[]): Timesheet[] {
        timesheets.forEach((timesheet) => this.parseTimesheetStatus(timesheet));
        return timesheets;
    }

    parseTimesheetStatus(timesheet: Timesheet): Timesheet {
        timesheet.status = TIMESHEET_STATUS.getStatus(timesheet.status.toString());
        return timesheet;
    }

    updateHappinessScore(timesheetUuid: string, happinessScore: number) {
        let url = `${environment.baseUrl}/timesheets/${timesheetUuid}/happiness-score`;
        if (happinessScore !== null) url += `?score=${happinessScore}`;
        return this.httpClient.put<Timesheet>(url, {}, this.httpOptions);
    }

    getTimeSheetsForProject(projectId: string) {
        return this.httpClient.get<Timesheet[]>(`${environment.baseUrl}/timesheets/project/${projectId}`, this.httpOptions);
    }

    fetchTimesheetsForEmployee(employeeId: string) {
        return this.httpClient.get<Timesheet[]>(`${environment.baseUrl}/timesheets/employee/${employeeId}`, this.httpOptions);
    }

    changeTimesheetStatus(timesheetId: string, status: TIMESHEET_STATUS) {
        return this.httpClient.patch(`${environment.baseUrl}/timesheets/${timesheetId}/update-status/${status.key}`, {}, this.httpOptions);
    }

    updateRequiredHours(timesheetId: string, hours: number) {
        return this.httpClient.patch(`${environment.baseUrl}/timesheets/${timesheetId}/required-hours/${hours}`, {}, this.httpOptions);
    }

    fetchTimesheetsByProjectIdAndYearAndMonth(projectId: string, year: number, month: number) {
        return this.httpClient.get<Timesheet[]>(`${environment.baseUrl}/timesheets/project/${projectId}?month=${month}&year=${year}`, this.httpOptions);
    }

    fetchAllProjectsProtected(employeeUuid: string) {
        return this.httpClient.get<Project[]>(
            `${environment.baseUrl}/timesheets${this.getCorrectRoute()}/all-projects-protected${
                this.getTokenIfExists() != '' ? this.getTokenIfExists() : '/' + employeeUuid
            }`,
            this.httpOptions,
        );
    }
}
